import { useTheme } from '@emotion/react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ReactComponent as BackArrow } from 'src/assets/svg/back-arrow.svg';
import { ReactComponent as CloseIcon } from 'src/assets/svg/close-icon.svg';
import { Section } from 'src/components/section';
import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { Button } from 'src/components/button';
import { changePasswordRequest, loginUser } from 'src/auth/auth0';

import { forgottenPasswordDataKeys } from '../constants';
import { forgottenPasswordDataSchema } from '../login-schema';

import { styles } from './styles';

import type { ForgottenPasswordFormProps } from './types';
import type { ForgottenPasswordDataFormValues } from '../types';

export const ForgottenPasswordForm: FC<ForgottenPasswordFormProps> = ({
    closeFn,
    handleShowPasswordReset,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [serverError, setServerError] = useState<string | null>(null);
    const [emailSent, setEmailSent] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { control, handleSubmit, formState, watch } = useForm<ForgottenPasswordDataFormValues>({
        resolver: yupResolver(
            forgottenPasswordDataSchema
        ) as Resolver<ForgottenPasswordDataFormValues>,
        defaultValues: { email: '', password: '' },
        context: { emailSent },
    });

    const { isValid } = formState;
    const watchEmailField = watch('email');
    const watchPasswordField = watch('password');

    const handleClose = () => {
        setEmailSent(false);
        closeFn();
    };

    const onResetPassword = handleSubmit(async (data) => {
        setLoading(true);

        const { email } = data;
        try {
            await changePasswordRequest(email);
            setEmailSent(true);
        } catch (error) {
            setServerError(error.message);
        } finally {
            setLoading(false);
        }
    });

    const onLoginUser = handleSubmit(async (data) => {
        const { email, password } = data;
        try {
            await loginUser(email, password);
        } catch (error) {
            setServerError(error.message);
        }
    });

    const handleSubmitOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (!emailSent) {
                if (watchEmailField) onResetPassword();
            } else if (watchEmailField && watchPasswordField) onLoginUser();
        }
    };

    return (
        <Box css={styles.forgottenPasswordWrapper}>
            <Box>
                <Box css={styles.back} onClick={handleShowPasswordReset}>
                    <BackArrow css={styles.backArrowIcon} />
                    <Typography>{t('agreements_back_button_content')}</Typography>
                </Box>
            </Box>
            <Section
                header={
                    !emailSent
                        ? t('login_dialog_forgotten_password')
                        : t('forgotten_password_email_sent_title')
                }
            >
                {serverError && (
                    <Box css={styles.loginDialogErrorBox}>
                        <Typography color={theme.palette.primary.main} variant="subtitle1">
                            {serverError}
                        </Typography>
                    </Box>
                )}

                <Typography css={styles.forgottenPasswordSubtitle} variant="subtitle1">
                    {!emailSent
                        ? t('forgotten_password_text')
                        : t('forgotten_password_email_sent_text')}
                </Typography>

                <Stack
                    spacing="20px"
                    css={styles.forgottenPasswordFormWrapper}
                    onKeyDown={handleSubmitOnEnter}
                >
                    <ControlledField
                        type="email"
                        name={forgottenPasswordDataKeys.email}
                        control={control}
                        label={t('login_dialog_email_address')}
                        placeholder={t('login_dialog_email_address')}
                    />
                    {emailSent && (
                        <ControlledField
                            fieldType={FieldTypes.Password}
                            name={forgottenPasswordDataKeys.password}
                            control={control}
                            label={t('login_dialog_password')}
                            placeholder={t('login_dialog_password')}
                        />
                    )}
                </Stack>
                {!emailSent ? (
                    <Button
                        disableElevation
                        css={styles.forgottenPasswordButton}
                        variant="contained"
                        onClick={onResetPassword}
                        loading={loading}
                        disabled={!watchEmailField}
                    >
                        {t('forgotten_password_cta')}
                    </Button>
                ) : (
                    <Button
                        disableElevation
                        disabled={!isValid}
                        css={styles.forgottenPasswordButton}
                        variant="contained"
                        onClick={onLoginUser}
                    >
                        {t('login_dialog_cta')}
                    </Button>
                )}
            </Section>

            <IconButton css={styles.forgottenPasswordCloseButton} onClick={handleClose}>
                <CloseIcon />
            </IconButton>
        </Box>
    );
};
